import * as React from 'react';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/utils/shadcn';

const RadioGroup = React.forwardRef<
    React.ComponentRef<typeof RadioGroupPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
    return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioGroupItemVariants = cva(
    'focus-visible:ring-primary-600 aspect-square border ring-offset-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            variant: {
                default: 'border-primary-700/60 text-primary-700 size-4 rounded-full p-0.5',
                badge: 'min-h-6 rounded-full p-0.5',
            },
            theme: {
                default: 'text-gray-950 dark:text-gray-300',
                primary: 'border-primary-700 bg-primary-300 text-primary-700',
                slate: 'border-slate-700 bg-slate-300 text-slate-700',
                gray: 'border-gray-700 bg-gray-300 text-gray-700',
                red: 'border-red-700 bg-red-300 text-red-700',
                orange: 'border-orange-700 bg-orange-300 text-orange-700',
                amber: 'border-amber-700 bg-amber-300 text-amber-700',
                yellow: 'border-yellow-700 bg-yellow-300 text-yellow-700',
                lime: 'border-lime-700 bg-lime-300 text-lime-700',
                green: 'border-green-700 bg-green-300 text-green-700',
                emerald: 'border-green-700 bg-emerald-300 text-emerald-700',
                teal: 'border-teal-700 bg-teal-300 text-teal-700',
                cyan: 'border-cyan-700 bg-cyan-300 text-cyan-700',
                sky: 'border-sky-700 bg-sky-300 text-sky-700',
                blue: 'border-blue-700 bg-blue-300 text-blue-700',
                indigo: 'border-indigo-700 bg-indigo-300 text-indigo-700',
                violet: 'border-violet-700 bg-violet-300 text-violet-700',
                purple: 'border-purple-700 bg-purple-300 text-purple-700',
                fuchsia: 'border-fuchsia-700 bg-fuchsia-300 text-fuchsia-700',
                pink: 'border-pink-700 bg-pink-300 text-pink-700',
                rose: 'border-rose-700 bg-rose-300 text-rose-700',
            },
        },
    },
);

const radioGroupItemIndicator = cva('', {
    variants: {
        variant: {
            default: 'bg-primary-700 aspect-square size-full rounded-full',
            badge: '',
        },
        theme: {
            default: 'bg-gray-950 dark:bg-gray-300',
            primary: 'bg-primary-700',
            slate: 'bg-slate-700',
            gray: 'bg-gray-700',
            red: 'bg-red-700',
            orange: 'bg-orange-700',
            amber: 'bg-amber-700',
            yellow: 'bg-yellow-700',
            lime: 'bg-lime-700',
            green: 'bg-green-700',
            emerald: 'bg-emerald-700',
            teal: 'bg-teal-700',
            cyan: 'bg-cyan-700',
            sky: 'bg-sky-700',
            blue: 'bg-blue-700',
            indigo: 'bg-indigo-700',
            violet: 'bg-violet-700',
            purple: 'bg-purple-700',
            fuchsia: 'bg-fuchsia-700',
            pink: 'bg-pink-700',
            rose: 'bg-rose-700',
        },
    },
});

const RadioGroupItem = React.forwardRef<
    React.ComponentRef<typeof RadioGroupPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & VariantProps<typeof radioGroupItemVariants>
>(({ className, theme, variant = 'default', ...props }, ref) => {
    return (
        <RadioGroupPrimitive.Item
            ref={ref}
            className={cn(radioGroupItemVariants({ theme, variant }), className)}
            {...props}
        >
            <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                <div className={cn(radioGroupItemIndicator({ theme, variant }))} />
            </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
    );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
